import React, { useMemo, useState } from 'react';
import { Mafs, Coordinates, Polygon, Text, Theme } from 'mafs';
import { InputNumber, Switch } from 'antd';

const clampInt = (raw, fallback, min, max) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    const asInt = Math.round(n);
    return Math.max(min, Math.min(max, asInt));
};

export const RatioModelWidget = ({ config = {}, variables = {} }) => {
    const {
        items: configItems = [],
        scaleFactor: configScaleFactor,
        showTotal: configShowTotal = true,
    } = config;

    // Local interactive controls (still allow user adjustment)
    const [localScaleFactor, setLocalScaleFactor] = useState(
        clampInt(configScaleFactor ?? 1, 1, 1, 100)
    );
    const [showTotal, setShowTotal] = useState(Boolean(configShowTotal));

    // Variable override (null-safe); then clamp
    const scaleFactor = useMemo(() => {
        const raw = variables.scaleFactor ?? localScaleFactor ?? 1;
        return clampInt(raw, 1, 1, 100);
    }, [variables.scaleFactor, localScaleFactor]);

    // Defensive item normalization (avoids NaN / 0-width segments)
    const items = useMemo(() => {
        const arr = Array.isArray(configItems) ? configItems : [];
        return arr.map((it, idx) => {
            const label =
                (typeof it?.label === 'string' && it.label.trim()) ? it.label.trim() : `Part ${idx + 1}`;
            const value = clampInt(it?.value ?? 1, 1, 1, 200); // cap per-part to avoid insane DOM
            return { label, value };
        });
    }, [configItems]);

    const totalRatioParts = useMemo(
        () => items.reduce((sum, item) => sum + item.value, 0),
        [items]
    );
    const totalValue = useMemo(
        () => totalRatioParts * scaleFactor,
        [totalRatioParts, scaleFactor]
    );

    // Layout
    const blockWidth = 1.0;
    const totalDrawingWidth = totalRatioParts * blockWidth;
    const padding = Math.max(2, totalDrawingWidth * 0.1);
    const barHeight = 0.8;
    const barY = 0;

    const renderElements = useMemo(() => {
        let currentX = 0;
        const out = [];

        items.forEach((item, itemIndex) => {
            const startX = currentX;
            const endX = startX + item.value * blockWidth;
            const color = `hsl(${itemIndex * 90}, 70%, 50%)`;

            for (let i = 0; i < item.value; i++) {
                const segmentStartX = startX + i * blockWidth;
                const segmentEndX = segmentStartX + blockWidth;

                out.push(
                    <Polygon
                        key={`block-${itemIndex}-${i}`}
                        points={[
                            [segmentStartX, barY - barHeight / 2],
                            [segmentEndX, barY - barHeight / 2],
                            [segmentEndX, barY + barHeight / 2],
                            [segmentStartX, barY + barHeight / 2],
                        ]}
                        color={color}
                        fillOpacity={0.6}
                        strokeOpacity={1}
                        weight={1}
                        svgPolygonProps={{ stroke: Theme.foreground }}
                    />
                );
            }

            out.push(
                <Text
                    key={`label-${itemIndex}`}
                    x={(startX + endX) / 2}
                    y={barY + barHeight / 2 + 0.5}
                    size={16}
                    weight="bold"
                    color={Theme.text}
                >
                    {item.label}
                </Text>
            );

            out.push(
                <Text
                    key={`value-${itemIndex}`}
                    x={(startX + endX) / 2}
                    y={barY - barHeight / 2 - 0.5}
                    size={14}
                    color={Theme.text}
                >
                    {(item.value * scaleFactor).toLocaleString()}
                </Text>
            );

            currentX = endX;
        });

        return out;
    }, [items, scaleFactor]);

    return (
        <div className="flexColumn mediumGap ch-width-100">
            <div className="ch-emph-block bg-slate-50 border-all--s rounded-lg">
                <div style={{ height: 280, position: 'relative' }}>
                    <Mafs
                        viewBox={{ x: [-padding, totalDrawingWidth + padding], y: [-3, 2] }}
                        pan={false}
                        zoom={false}
                    >
                        <Coordinates.Cartesian
                            xAxis={{ visible: false }}
                            yAxis={{ visible: false }}
                            subdivisions={false}
                        />

                        {renderElements}

                        {showTotal && (
                            <Text x={totalDrawingWidth / 2} y={-2.5} size={18} weight="bold" color={Theme.blue}>
                                Total: {totalValue.toLocaleString()}
                            </Text>
                        )}
                    </Mafs>
                </div>
            </div>

            <div className="ch-card padding--m bg-white shadow-sm border-all--s">
                <div className="flexColumn gap-medium">
                    <div className="flexSpaceBetween flexAlignCentre">
                        <span className="ch-text-bold text-slate-700">Scale Factor</span>
                        <InputNumber
                            min={1}
                            max={100}
                            value={localScaleFactor}
                            onChange={(v) => setLocalScaleFactor(clampInt(v, 1, 1, 100))}
                            style={{ width: 90 }}
                        />
                    </div>

                    <div className="flexSpaceBetween flexAlignCentre">
                        <span className="ch-text-bold text-slate-700">Show Total</span>
                        <Switch checked={showTotal} onChange={setShowTotal} />
                    </div>
                </div>
            </div>
        </div>
    );
};
